import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { buttonVariants } from '@/components/ui/button-variants';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import type { APIKeyActivity } from '@/types/auditLog';

interface Props {
  keyId: number | null;
  onClose: () => void;
}

export function ApiKeyActivityDialog({ keyId, onClose }: Props) {
  // Keyed by key id so a previous key's activity is never shown while loading.
  const [loaded, setLoaded] = useState<{ id: number; activity?: APIKeyActivity; error?: string } | null>(null);

  useEffect(() => {
    if (keyId === null) return;
    api.apiKeys
      .activity(keyId)
      .then((activity) => setLoaded({ id: keyId, activity }))
      .catch((err) => setLoaded({ id: keyId, error: err instanceof Error ? err.message : 'Failed to load activity' }));
  }, [keyId]);

  const current = loaded && loaded.id === keyId ? loaded : null;
  const activity = current?.activity ?? null;
  const error = current?.error ?? null;
  const linkClass = buttonVariants({ variant: 'outline', size: 'sm' });
  const { captureOpener, restoreFocus } = useDialogFocusRestore();

  return (
    <Dialog open={keyId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        onOpenAutoFocus={captureOpener}
        onCloseAutoFocus={restoreFocus}
      >
        <DialogHeader>
          <DialogTitle>Activity{activity ? `: ${activity.name}` : ''}</DialogTitle>
          <DialogDescription>
            Last used: {activity?.last_used_at ? new Date(activity.last_used_at).toLocaleString() : 'never'}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}
        {!activity && !error && <div className="text-sm text-muted-foreground">Loading…</div>}
        {activity && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Requests 24h</div>
                <div className="text-xl font-semibold">{activity.requests_24h}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Requests 7d</div>
                <div className="text-xl font-semibold">{activity.requests_7d}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground">Error rate 7d</div>
                <div className="text-xl font-semibold">{(activity.error_rate_7d * 100).toFixed(1)}%</div>
              </div>
            </div>
            <div>
              <div className="mb-1 font-medium">Top routes (7d)</div>
              {activity.top_routes.length === 0 ? (
                <div className="text-muted-foreground">No requests.</div>
              ) : (
                <ul className="space-y-1 font-mono text-xs">
                  {activity.top_routes.map((r) => (
                    <li key={`${r.method} ${r.route}`} className="flex justify-between">
                      <span>
                        {r.method} {r.route}
                      </span>
                      <span>{r.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="mb-1 font-medium">Recent mutations</div>
              {activity.recent_mutations.length === 0 ? (
                <div className="text-muted-foreground">None.</div>
              ) : (
                <ul className="space-y-1 text-xs">
                  {activity.recent_mutations.map((m) => (
                    <li key={m.id} className="flex justify-between">
                      <span className="font-mono">
                        {m.action}
                        {m.resource_id !== null && ` #${m.resource_id}`}
                      </span>
                      <span className={m.success ? '' : 'text-destructive'}>
                        {m.status_code} · {new Date(m.created_at).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex gap-2">
              <Link className={linkClass} to={`/audit-logs?api_key_id=${activity.api_key_id}`}>
                Open in Audit Logs
              </Link>
              <Link className={linkClass} to={`/audit-logs?api_key_id=${activity.api_key_id}&tab=requests`}>
                All requests
              </Link>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
